import React, { useState, useEffect } from 'react';
import { Shield, Monitor, Clock, Mail, Lock, X, Check } from 'lucide-react';

/**
 * Modal de Regras de Segurança
 * Aparece no primeiro login do usuário
 * Usuário clica em "Ciente" e não aparece mais
 */
const SecurityRulesModal = ({ isOpen, onClose, onAccept }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Animação de entrada
      setTimeout(() => setIsVisible(true), 50);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleAccept = () => {
    // Salvar no localStorage que o usuário já viu as regras
    localStorage.setItem('security_rules_accepted', 'true');
    localStorage.setItem('security_rules_accepted_at', new Date().toISOString());
    onAccept?.();
    onClose();
  };

  return (
    <div 
      className={`fixed inset-0 z-[100] flex items-center justify-center transition-all duration-300 ${
        isVisible ? 'bg-black/60 backdrop-blur-sm' : 'bg-transparent'
      }`}
      onClick={(e) => e.target === e.currentTarget && null} // Não fechar ao clicar fora
    >
      <div 
        className={`relative w-full max-w-lg mx-4 transition-all duration-300 ${
          isVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'
        }`}
      >
        {/* Card principal */}
        <div className="bg-gradient-to-br from-stone-900 via-stone-900 to-stone-800 rounded-2xl shadow-2xl border border-amber-500/20 overflow-hidden">
          
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500/10 to-amber-600/10 px-6 py-5 border-b border-amber-500/20">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
                <Shield className="w-6 h-6 text-stone-900" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Regras de Segurança</h2>
                <p className="text-sm text-stone-400">Para sua proteção no Author's Key</p>
              </div>
            </div>
          </div>

          {/* Conteúdo */}
          <div className="px-6 py-5 space-y-4">
            
            {/* Regra 1: Dispositivos */}
            <div className="flex items-start gap-4 p-4 bg-stone-800/50 rounded-xl border border-stone-700/50">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                <Monitor className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="text-white font-medium">Máximo 2 dispositivos</h3>
                <p className="text-sm text-stone-400 mt-1">
                  Você pode estar conectado em até 2 dispositivos simultaneamente. 
                  Ao conectar um terceiro, o mais antigo será desconectado automaticamente.
                </p>
              </div>
            </div>

            {/* Regra 2: Expiração */}
            <div className="flex items-start gap-4 p-4 bg-stone-800/50 rounded-xl border border-stone-700/50">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="text-white font-medium">Sessão expira em 10 dias</h3>
                <p className="text-sm text-stone-400 mt-1">
                  Por segurança, sua sessão expira automaticamente após 10 dias de inatividade. 
                  Você precisará fazer login novamente.
                </p>
              </div>
            </div>

            {/* Regra 3: Alerta de novo IP */}
            <div className="flex items-start gap-4 p-4 bg-stone-800/50 rounded-xl border border-stone-700/50">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h3 className="text-white font-medium">Alerta de novo dispositivo</h3>
                <p className="text-sm text-stone-400 mt-1">
                  Você receberá um email sempre que sua conta for acessada de um novo dispositivo ou localização.
                </p>
              </div>
            </div>

            {/* Regra 4: Tela de descanso */}
            <div className="flex items-start gap-4 p-4 bg-stone-800/50 rounded-xl border border-stone-700/50">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <Lock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="text-white font-medium">Tela de descanso com senha</h3>
                <p className="text-sm text-stone-400 mt-1">
                  Após 9 minutos de inatividade, sua tela será bloqueada. 
                  Você precisará digitar sua senha para continuar.
                </p>
              </div>
            </div>

          </div>

          {/* Footer */}
          <div className="px-6 py-5 bg-stone-800/30 border-t border-stone-700/50">
            <p className="text-xs text-stone-500 text-center mb-4">
              Ao continuar, você concorda com estas políticas de segurança.
            </p>
            <button
              onClick={handleAccept}
              className="w-full py-3 px-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-stone-900 font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20"
              data-testid="security-rules-accept-btn"
            >
              <Check className="w-5 h-5" />
              Estou ciente, continuar
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

/**
 * Hook para verificar se deve mostrar o modal de regras
 */
export const useSecurityRulesModal = () => {
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    // Verificar se o usuário já aceitou as regras
    const accepted = localStorage.getItem('security_rules_accepted');
    if (!accepted) {
      // Mostrar modal após um pequeno delay para não conflitar com outras animações
      const timer = setTimeout(() => setShowModal(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const closeModal = () => setShowModal(false);

  return { showModal, closeModal };
};

export default SecurityRulesModal;
